import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, map, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import type { Address, AddressListResponse as AddressList } from '@kin/property-tax-types';

export type { Address };

/** The address fields the display formatters need; satisfied by `Address` and the sign-up address. */
export type AddressParts = Pick<Address, 'street' | 'city' | 'state' | 'zip'> & { unit?: string | null };

/** e.g. "123 Main St, Unit 4" */
export function formatStreet(address: AddressParts): string {
  return address.unit ? `${address.street}, ${address.unit}` : address.street;
}

/** e.g. "Houston, TX 77002" */
export function formatCityStateZip(address: AddressParts): string {
  return `${address.city}, ${address.state} ${address.zip}`;
}

@Injectable({ providedIn: 'root' })
export class AddressService {
  private http = inject(HttpClient);

  private _address: Address | null = null;

  get address(): Address | null {
    return this._address;
  }

  get displayStreet(): string {
    return this._address ? formatStreet(this._address) : '';
  }

  get displayCityStateZip(): string {
    return this._address ? formatCityStateZip(this._address) : '';
  }

  loadAddress(): Observable<Address | null> {
    if (this._address) return of(this._address);
    return this.http.get<AddressList>(`${environment.apiUrl}/address`).pipe(
      map(res => {
        if (res.addresses.length === 1) {
          this._address = res.addresses[0];
          return this._address;
        }
        return null;
      }),
      catchError(() => of(null)),
    );
  }

  clear(): void {
    this._address = null;
  }
}
